import _ from "lodash";
import { z } from "zod";
import {
  AggregateQuery,
  ClassificationQuery,
  ComputedQuery,
  CorePipelineOutput,
  CustomSchemaField,
  CustomSchemaFieldBase,
  CustomSchemaFieldResult,
  ExtractionQuery,
  ExtractionType,
  LLMExtractionQuery,
  ParsingType,
  PipelineConfig,
  PredefinedQuery,
  Query,
  RegexExtractionQuery,
  RoutedQueries,
} from "../../../interfaces/doc";
import {
  AddressSchema,
  CurrencySchema,
  ExtractionResultBaseSchema,
  MeasureSchema,
  NameSchema,
} from "../../../schemas/OrionExtraction";
import AddressFormFields from "./AddressFields";
import CheckboxFormFields from "./CheckboxField";
import CurrencyFormFields from "./CurrencyFields";
import getCustomFormField from "./CustomFields";
import DateFormFields from "./DateField";
import getEnumFormField from "./EnumField";
import FloatFormFields from "./FloatField";
import IntFormFields from "./IntField";
import MeasureFormFields from "./MeasureFields";
import NameFormFields from "./NameFields";
import StringFormFields from "./StringField";

export const getQueryByName = <T extends Query>(
  queries: (T | RoutedQueries<T>)[],
  key: string
) => {
  for (const query of queries) {
    if ("id" in query) {
      if (query.name === key) {
        return query;
      }
    } else if ("queries" in query) {
      const found = query.queries.find((q) => q.name === key);
      if (found) {
        return found;
      }
    }
  }

  throw new Error(`Query with name ${key} not found`);
};

export const getQueryById = <T extends Query>(
  queries: (T | RoutedQueries<T>)[],
  key: string
) => {
  for (const query of queries) {
    if ("id" in query) {
      if (query.id === key) {
        return query;
      }
    } else if ("queries" in query) {
      const found = query.queries.find((q) => q.id === key);
      if (found) {
        return found;
      }
    }
  }

  throw new Error(`Query with id ${key} not found`);
};

const flatMapQueries = <T extends Query>(queries: (T | RoutedQueries<T>)[]) => {
  return queries.flatMap((query) => {
    if ("queries" in query) {
      return query.queries;
    } else {
      return [query];
    }
  });
};

/**
 * Function to filter out extraction query based on classification and split result
 */
export const filterExtractionQueries = <T extends Query>(
  queries: (T | RoutedQueries<T>)[],
  classification: Record<string, string | undefined> | null
) => {
  const classifications = classification
    ? Object.entries(classification)
        .filter(([key, result]) => !!result)
        .map(([key, result]) =>
          key === "split"
            ? `${key}.${result}`
            : `classification.${key}.${result}`
        )
    : [];

  return flatMapQueries(
    queries.filter(
      (query) =>
        !("queries" in query) ||
        !query.routes.some((route) => !classifications.includes(route))
    )
  );
};

/**
 * Function to filter out extraction query based on classification and split result and not editable querie (computed/aggregates)
 */
export const filterDisplayExtractionQueries = <T extends Query>(
  queries: (T | RoutedQueries<T>)[],
  classification: Record<string, string | undefined> | null
) => {
  return filterExtractionQueries(queries, classification).filter(
    (query) =>
      query.type !== ExtractionType.AGGREGATE &&
      query.type !== ExtractionType.COMPUTED
  );
};

/**
 * Function to filter out classification query based on split result
 */
export const filterClassificationQueries = (
  queries: (ClassificationQuery | RoutedQueries<ClassificationQuery>)[],
  split: string | undefined | null
) => {
  return flatMapQueries(
    queries.filter(
      (query) =>
        !("queries" in query) || !query.routes.includes(`split.${split}`)
    )
  );
};

export const getResultDefault: (
  parsing_type: ParsingType,
  custom_schema: CustomSchemaFieldResult[] | null
) => {} | undefined = (parsing_type, custom_schema) => {
  switch (parsing_type) {
    case ParsingType.Name:
      return { firstname: undefined, lastname: undefined, civility: undefined };
    case ParsingType.Address:
      return {
        number: undefined,
        way: undefined,
        postal_code: undefined,
        city: undefined,
        country: undefined,
      };
    case ParsingType.String:
      return undefined;
    case ParsingType.Float:
      return undefined;
    case ParsingType.Int:
      return undefined;
    case ParsingType.Date:
      return undefined;
    case ParsingType.Measure:
      return { value: undefined, unit: undefined };
    case ParsingType.Currency:
      return { value: undefined, currency: undefined };
    case ParsingType.Boolean:
      return undefined;
    case ParsingType.Custom:
      return Object.fromEntries(
        custom_schema!.map(({ name, parsing_type }) => [
          name,
          getResultDefault(parsing_type, null),
        ])
      );
    case ParsingType.Enum:
      return undefined;
    default:
      return undefined;
  }
};

function getPredefinedDefinition(
  query: PredefinedQuery
): [ParsingType, CustomSchemaFieldResult[] | null] {
  switch (query.query) {
    case "EPC":
      return [
        ParsingType.Custom,
        [
          {
            name: "energy_score",
            parsing_type: ParsingType.Enum,
            enum_values: ["A", "B", "C", "D", "E", "F", "G"],
          },
          {
            name: "ghg_score",
            parsing_type: ParsingType.Enum,
            enum_values: ["A", "B", "C", "D", "E", "F", "G"],
          },
          {
            name: "primary_energy_consumption",
            parsing_type: ParsingType.Measure,
            enum_values: null,
          },
          {
            name: "final_energy_consumption",
            parsing_type: ParsingType.Measure,
            enum_values: null,
          },
          {
            name: "ghg_emission",
            parsing_type: ParsingType.Measure,
            enum_values: null,
          },
        ],
      ];
    case "ASBESTOS":
      return [ParsingType.Boolean, null];
    case "TWODDOC":
      return [
        ParsingType.Custom,
        [
          {
            name: "Numéro de référence",
            parsing_type: ParsingType.String,
            enum_values: null,
          },
          { name: "Année", parsing_type: ParsingType.Int, enum_values: null },
          {
            name: "Nombre de parts",
            parsing_type: ParsingType.Float,
            enum_values: null,
          },
          {
            name: "Déclarant 1",
            parsing_type: ParsingType.String,
            enum_values: null,
          },
          {
            name: "Numéro fiscal déclarant 1",
            parsing_type: ParsingType.Int,
            enum_values: null,
          },
          {
            name: "Déclarant 2",
            parsing_type: ParsingType.String,
            enum_values: null,
          },
          {
            name: "Numéro fiscal déclarant 2",
            parsing_type: ParsingType.Int,
            enum_values: null,
          },
          {
            name: "Date du paiement",
            parsing_type: ParsingType.Date,
            enum_values: null,
          },
          {
            name: "Revenue de référence",
            parsing_type: ParsingType.Int,
            enum_values: null,
          },
        ],
      ];
    default:
      return [ParsingType.String, null];
  }
}

function getPredefinedResult(query: PredefinedQuery) {
  const [parsing_type, custom_schema] = getPredefinedDefinition(query);
  return {
    name: query.name,
    value: getResultDefault(parsing_type, custom_schema),
    check: null,
    expected_value: query.expected_value,
    enum_values: null,
    custom_schema: custom_schema,
    parsing_type: parsing_type,
  };
}

function getAggregateComputedResult(query: ComputedQuery | AggregateQuery) {
  return {
    name: query.name,
    value: getResultDefault(ParsingType.String, null),
    check: null,
    expected_value: null,
    enum_values: null,
    custom_schema: null,
    parsing_type: ParsingType.String,
  };
}

function getLLMRegexResult(query: RegexExtractionQuery | LLMExtractionQuery) {
  if (query.many) {
    return {
      name: query.name,
      value: [getResultDefault(query.parsing_type, query.custom_schema)],
      check: null,
      expected_value: query.expected_value,
      enum_values: query.enum_values,
      custom_schema: query.custom_schema,
      parsing_type: query.parsing_type,
    };
  } else {
    return {
      name: query.name,
      value: getResultDefault(query.parsing_type, query.custom_schema),
      check: null,
      expected_value: query.expected_value,
      enum_values: query.enum_values,
      custom_schema: query.custom_schema,
      parsing_type: query.parsing_type,
    };
  }
}

function getExtractionResult(query: ExtractionQuery) {
  if (query.type === ExtractionType.PREDEFINED) {
    return getPredefinedResult(query);
  } else if (
    query.type === ExtractionType.COMPUTED ||
    query.type === ExtractionType.AGGREGATE
  ) {
    return getAggregateComputedResult(query);
  } else {
    return getLLMRegexResult(query);
  }
}

export function getCompleteOutput(
  config: PipelineConfig,
  output: { [key: string]: any },
  reviewedOutput: { [key: string]: any }
) {
  const completeOutput: { [key: string]: any } = {
    classification: config.split || config.classification ? {} : null,
    extraction: config.extraction ? {} : null,
  };

  if (config.classification || config.split) {
    completeOutput["classification"] = Object.fromEntries([
      ...(config.split ? [["split", ""]] : []),
      ...(config.classification?.queries.flatMap((query) => {
        if ("queries" in query) {
          return query.queries.map((nestedQuery) => [nestedQuery.name, ""]);
        } else {
          return [[query.name, ""]];
        }
      }) ?? []),
    ]);
  }
  if (config.extraction) {
    completeOutput["extraction"] = Object.fromEntries(
      config.extraction.queries.flatMap((query) => {
        if ("queries" in query) {
          return query.queries.map((nestedQuery) => [
            nestedQuery.id,
            getExtractionResult(nestedQuery),
          ]);
        } else {
          return [[query.id, getExtractionResult(query)]];
        }
      })
    );
  }
  return _.merge(completeOutput, output, reviewedOutput);
}

export function fillMissingExtraction(result: CorePipelineOutput) {
  return {
    classification: result.classification,
    extraction: result.extraction
      ? Object.fromEntries(
          Object.entries(result.extraction).map(([key, result]) => {
            if (result) {
              return [
                key,
                {
                  ...result,
                  value:
                    result.value === null
                      ? getResultDefault(
                          result.parsing_type,
                          result.custom_schema
                        )
                      : result.value,
                },
              ];
            }
            return [key, result];
          })
        )
      : result.extraction,
  };
}

const getValueSchema: (
  parsing_type: ParsingType,
  enum_values?: string[] | null,
  custom_schema?:
    | CustomSchemaFieldBase[]
    | CustomSchemaField[]
    | CustomSchemaFieldResult[]
    | null
) => z.ZodType = (parsing_type, enum_values, custom_schema) => {
  switch (parsing_type) {
    case ParsingType.Name:
      return NameSchema;
    case ParsingType.Address:
      return AddressSchema;
    case ParsingType.String:
      return z.string().nullable().optional();
    case ParsingType.Float:
      return z.coerce.number().nullable().optional();
    case ParsingType.Int:
      return z.coerce.number().nullable().optional();
    case ParsingType.Date:
      return z.string().date().nullable().optional();
    case ParsingType.Measure:
      return MeasureSchema;
    case ParsingType.Currency:
      return CurrencySchema;
    case ParsingType.Boolean:
      return z.boolean().nullable().optional();
    case ParsingType.Custom:
      return custom_schema
        ? z.object(
            Object.fromEntries(
              (custom_schema as CustomSchemaFieldResult[]).map(
                ({ name, parsing_type, enum_values }) => [
                  name,
                  getValueSchema(parsing_type, enum_values, null),
                ]
              )
            )
          )
        : z.any().nullable().optional();
    case ParsingType.Enum:
      return enum_values
        ? z
            .enum(enum_values as [string, ...string[]])
            .nullable()
            .optional()
        : z.any().nullable().optional();
    default:
      return z.any().nullable().optional();
  }
};

function getPredefinedSchema(query: PredefinedQuery): z.ZodType {
  const [parsing_type, custom_schema] = getPredefinedDefinition(query);
  return getValueSchema(parsing_type, undefined, custom_schema);
}

function getQueryResultSchema(query: ExtractionQuery): z.ZodType {
  if (query.type === ExtractionType.PREDEFINED) {
    const valueSchema = getPredefinedSchema(query);
    return ExtractionResultBaseSchema.extend({
      value: valueSchema,
      possible_answers: valueSchema.array().nullable().optional(),
    });
  } else if (
    query.type === ExtractionType.COMPUTED ||
    query.type === ExtractionType.AGGREGATE
  ) {
    return ExtractionResultBaseSchema.extend({
      value: z.any().nullable().optional(),
      possible_answers: z.any().nullable().optional(),
    });
  } else {
    const valueSchema = getValueSchema(
      query.parsing_type,
      query.enum_values,
      query.custom_schema
    );
    if (query.many) {
      return ExtractionResultBaseSchema.extend({
        value: valueSchema.array(),
        possible_answers: valueSchema.array().nullable().optional(),
      });
    } else {
      return ExtractionResultBaseSchema.extend({
        value: valueSchema,
        possible_answers: valueSchema.array().nullable().optional(),
      });
    }
  }
}

export const getResultFormSchema = (config: PipelineConfig) => {
  const schemaObject = {
    classification:
      !!config.classification || !!config.split
        ? z.object(
            Object.fromEntries([
              ...(config.split
                ? [
                    [
                      "split",
                      z.enum([
                        ...config.split.segmentation.classes.map(
                          (classe) => classe.name
                        ),
                        ...(config.split.segmentation.default?.name
                          ? [config.split.segmentation.default.name]
                          : []),
                      ] as [string, ...string[]]),
                    ],
                  ]
                : []),
              ...(config.classification?.queries || []).flatMap((query) => {
                if ("queries" in query) {
                  return query.queries.map((nestedQuery) => [
                    nestedQuery.name,
                    z
                      .enum([
                        ...nestedQuery.classes.map((classe) => classe.name),
                        ...(nestedQuery.default?.name
                          ? [nestedQuery.default.name]
                          : []),
                      ] as [string, ...string[]])
                      .optional(),
                  ]);
                } else {
                  return [
                    [
                      query.name,
                      z
                        .enum([
                          ...query.classes.map((classe) => classe.name),
                          ...(query.default?.name ? [query.default.name] : []),
                        ] as [string, ...string[]])
                        .optional(),
                    ],
                  ];
                }
              }),
            ])
          )
        : z.null(),
    extraction: config.extraction
      ? z.object(
          Object.fromEntries(
            config.extraction.queries.flatMap((query) => {
              if ("queries" in query) {
                return query.queries.map((nestedQuery) => [
                  nestedQuery.id,
                  getQueryResultSchema(nestedQuery).optional(),
                ]);
              } else {
                return [[query.id, getQueryResultSchema(query).optional()]];
              }
            })
          )
        )
      : z.null(),
  };

  return z.object(schemaObject);
};

export const getField: (
  parsing_type: ParsingType,
  custom_schema: CustomSchemaField[] | null,
  enum_values: string[] | null
) => React.FC<{
  extractionId: string;
  extractionPath?: string | undefined;
  label?: string;
}> = (
  parsing_type: ParsingType,
  custom_schema: CustomSchemaField[] | null,
  enum_values: string[] | null
) => {
  switch (parsing_type) {
    case ParsingType.Name:
      return NameFormFields;
    case ParsingType.Address:
      return AddressFormFields;
    case ParsingType.Currency:
      return CurrencyFormFields;
    case ParsingType.Measure:
      return MeasureFormFields;
    case ParsingType.Date:
      return DateFormFields;
    case ParsingType.Boolean:
      return CheckboxFormFields;
    case ParsingType.Int:
      return IntFormFields;
    case ParsingType.Float:
      return FloatFormFields;
    case ParsingType.Enum:
      return getEnumFormField(enum_values!);
    case ParsingType.Custom:
      return getCustomFormField(custom_schema!);
    default:
      return StringFormFields;
  }
};
